import { ServiceItem } from '../types';
import { contractedQuantity } from './nepPosition';
import { bestandteilSperrText, heldByComponentsQuantity, openQuantity } from './projectLv';

/**
 * Wann ein Projekt sich SELBST auf BEENDET setzt.
 *
 * Seit dem 15.09.2026 ist das Ziel 'completed' (BEENDET), nicht mehr
 * 'finished' (ARCHIV): ins Archiv geht ein Projekt erst, wenn die
 * Schlussrechnung gestellt und bezahlt ist — und das entscheidet ein
 * Administrator von Hand (archiveGuardLogic, Regel 3). Der Automatismus sagt
 * nur noch: „hier ist nichts mehr zu holen, die Schlussrechnung kann
 * geschrieben werden".
 *
 * Anlass: wer die letzte offene Menge schliesst (Verzicht oder Ruecknahme),
 * hat damit gesagt, dass an diesem Projekt nichts mehr zu holen ist. Danach
 * noch einmal in das Projekt zu gehen und den Statusumschalter zu druecken ist
 * ein zweiter Handgriff fuer dieselbe Aussage.
 *
 * DIE GEFAHR dabei ist nicht das Archivieren, sondern das VERFRUEHTE
 * Archivieren. "Nichts offen" heisst fuer sich genommen wenig: ein frisch
 * angelegtes Projekt hat auf jeder Position `quantityUsed = 0` und damit
 * ebenfalls nichts Offenes. Ein laufendes Projekt, dessen bisherige Leistung
 * gerade vollstaendig abgerechnet ist, genauso. Beide Male waere das Archiv
 * falsch, und es faellt niemandem auf — das Projekt verschwindet einfach aus
 * der Arbeitsliste.
 *
 * Deshalb muessen DREI Aussagen zugleich gelten:
 *
 *   1. Es gibt ein Leistungsverzeichnis. Ohne Positionen ist jede Aussage
 *      ueber "nichts mehr offen" leer.
 *   2. An keiner Position ist noch etwas abrechenbar.
 *   3. An keiner Position steht noch beauftragte Leistung aus, die weder
 *      erbracht noch abgerechnet ist. Das ist der Riegel gegen das frische
 *      und gegen das laufende Projekt: dort ist die Vertragsmenge noch nicht
 *      aufgebraucht.
 *
 * Die Regel ist bewusst STRENG und nimmt in Kauf, in Zweifelsfaellen NICHT
 * auszuloesen. Ein nicht automatisch archiviertes Projekt kostet einen Klick;
 * ein zu frueh archiviertes verschwindet aus dem Blick, und die naechste
 * faellige Begehung faellt mit ihm aus.
 */

/** Mengen-Toleranz wie im uebrigen Rechnen des Moduls. */
const EPSILON = 0.0005;

export interface AutoArchiveVerdict {
    /** Soll der Status auf 'completed' (BEENDET) gesetzt werden? */
    archive: boolean;
    /**
     * Warum nicht — Klartext fuer die Rueckmeldung an den Anwender.
     *
     * Er bekommt sie zu sehen: „nichts passiert" ist bei einem Automatismus
     * die schlechteste Auskunft, weil sie wie ein Fehler aussieht und wie ein
     * Erfolg behandelt wird.
     */
    reason?: string;
}

export const autoArchiveVerdict = (
    services: ServiceItem[],
    currentStatus: string | undefined,
    /**
     * Übungsprojekt (docs/lernzentrum-tutorial-plan.md, 8.2: „Keine
     * Auto-Archivierung"). Das Archiv ist eine Aussage über ein echtes
     * Bauvorhaben; ein Übungsprojekt, das sich beim Üben von Verzicht oder
     * Rücknahme selbst ins Archiv setzt, verschwände aus dem Übungsplatz und
     * der Nutzer suchte es. Vorgabe ist FALSE — wer es nicht weiss, ändert
     * nichts am bisherigen Verhalten.
     */
    istUebung = false,
): AutoArchiveVerdict => {
    if (istUebung) {
        return { archive: false, reason: 'Übungsprojekte werden nicht automatisch archiviert.' };
    }
    if (currentStatus === 'finished') {
        return { archive: false, reason: 'Das Projekt steht bereits im Archiv.' };
    }
    if (currentStatus === 'completed') {
        return { archive: false, reason: 'Das Projekt ist bereits beendet.' };
    }
    if (!services || services.length === 0) {
        return { archive: false, reason: 'Das Projekt hat kein Leistungsverzeichnis.' };
    }

    const stillOpen = services.filter((s) => openQuantity(s) > EPSILON);
    if (stillOpen.length > 0) {
        return {
            archive: false,
            reason: `Es ist noch etwas abrechenbar (${stillOpen.length} Position${stillOpen.length === 1 ? '' : 'en'}).`,
        };
    }

    // PAKETPOSITION: erbracht, aber ZURUECKGEHALTEN (Bestandteile offen oder
    // Stand nicht ladbar). openQuantity sagt dort 0 — und die beauftragte
    // Menge ist mit der Buchung aufgebraucht, die naechste Pruefung saehe also
    // auch nichts mehr. Ohne diesen Halt setzte sich ein Projekt mit
    // unabgerechnetem Paket selbst auf BEENDET (Plan, Kernregel 4: fail
    // closed heisst „zurueckgehalten", nie „nichts offen").
    const held = services.filter((s) => heldByComponentsQuantity(s) > EPSILON);
    if (held.length > 0) {
        const namen = held
            .slice(0, 3)
            .map((s) => `${s.lvPosition || s.title}: ${bestandteilSperrText(s)}`)
            .join('; ');
        return {
            archive: false,
            reason:
                `An ${held.length} Paketposition${held.length === 1 ? '' : 'en'} ist erbrachte Leistung zurückgehalten, ` +
                `weil die Bestandteile noch nicht vollständig sind (${namen}${held.length > 3 ? ' …' : ''}).`,
        };
    }

    // Beauftragt, aber weder erbracht noch abgerechnet: da kommt noch Arbeit.
    // Gerechnet wird gegen die ERBRACHTE Menge und nicht gegen die
    // abgerechnete: eine erbrachte, aber verzichtete Leistung ist geleistete
    // Arbeit und darf das Archiv nicht aufhalten.
    // NEP: beauftragt ist nur, was gebucht wurde (contractedQuantity) — eine
    // unbestellte Eventualmenge ist keine Arbeit, die noch kommt.
    const outstanding = services.filter(
        (s) => contractedQuantity(s) - (s.quantityUsed || 0) > EPSILON,
    );
    if (outstanding.length > 0) {
        const namen = outstanding
            .slice(0, 3)
            .map((s) => s.lvPosition || s.title)
            .join(', ');
        return {
            archive: false,
            reason:
                `An ${outstanding.length} Position${outstanding.length === 1 ? '' : 'en'} steht noch beauftragte Leistung aus (${namen}${outstanding.length > 3 ? ' …' : ''}).`,
        };
    }

    return { archive: true };
};
